"use client";

import {
  IconListDetails,
  IconMoonStars,
  IconSun,
  IconSunset2,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { Reveal } from "../../../components/cambodia-food-expo/ui/Reveal";
import type { EventDay, EventPeriod, PeriodType } from "./types";
import { BlockTitle, UI, useText } from "./shared";
import { EventTimeline } from "./event-timeline";
import {
  EventFeatureBanner,
  EventPerformanceCards,
} from "./event-entertainment";
import { EventPhotoGallery } from "./event-photo-gallery";
import { EventVideoGallery } from "./event-video-gallery";

const PERIOD_ICONS: Record<PeriodType, typeof IconSun> = {
  morning: IconSun,
  afternoon: IconSunset2,
  evening: IconMoonStars,
};

export function EventDaySection({
  day,
  isLast,
}: {
  day: EventDay;
  isLast: boolean;
}) {
  const { text, isKhmer } = useText();
  return (
    <section
      id={day.id}
      aria-labelledby={`${day.id}-title`}
      className={cn(
        "event-day relative scroll-mt-32 py-16 md:py-24",
        day.number % 2 === 0 && "bg-[#0a2252]",
        isLast &&
          "bg-[radial-gradient(ellipse_at_top,rgba(71,127,221,0.18),transparent_55%)]",
      )}
    >
      <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-6">
        <Reveal>
          <header className="mb-12 border-b border-[var(--event-line)] pb-10 md:mb-16">
            <div>
              <p
                className={cn(
                  "text-sm font-semibold text-[var(--event-accent)]",
                  !isKhmer && "uppercase tracking-[0.22em]",
                )}
              >
                {text(UI.day)} {day.number} • {text(day.weekday)}
              </p>
              <h2
                id={`${day.id}-title`}
                className="mt-2 text-[1.8rem] font-bold leading-[1.45] text-[var(--event-ink)] md:text-[2.6rem]"
              >
                {text(day.dateLabel)}
              </h2>
              <p className="mt-2 text-base leading-7 text-[var(--event-muted)] md:text-lg">
                {text(day.theme)}
              </p>
            </div>
          </header>
        </Reveal>

        <div className="space-y-20 md:space-y-28">
          {day.periods.map((period) => (
            <EventPeriodSection key={period.id} period={period} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function EventPeriodSection({ period }: { period: EventPeriod }) {
  const { text, isKhmer } = useText();
  const Icon = PERIOD_ICONS[period.type];
  const dark =
    period.variant === "entertainment" || period.variant === "concert";

  const header = (
    <Reveal className="mb-8 md:mb-10">
      <p
        className={cn(
          "inline-flex items-center gap-2 text-sm font-semibold text-[var(--event-accent)]",
          !isKhmer && "uppercase tracking-[0.2em]",
        )}
      >
        <Icon className="size-5" aria-hidden="true" />
        {text(period.title)}
      </p>
      <h3 className="mt-2 text-[1.5rem] font-bold leading-[1.5] text-[var(--event-ink)] md:text-[2rem]">
        {text(period.subtitle)}
      </h3>
    </Reveal>
  );

  return (
    <section
      id={period.id}
      aria-label={`${text(period.title)} — ${text(period.subtitle)}`}
      className="event-period scroll-mt-36"
    >
      {header}

      {!dark && (
        <div className="max-w-4xl">
          <BlockTitle
            icon={<IconListDetails className="size-5" aria-hidden="true" />}
          >
            {text(UI.schedule)}
          </BlockTitle>
          <EventTimeline items={period.schedule} />
        </div>
      )}

      {dark && (
        <div className="space-y-10">
          {period.feature && (
            <Reveal>
              <EventFeatureBanner
                feature={period.feature}
                concert={period.variant === "concert"}
              />
            </Reveal>
          )}
          {period.featuredVideo && (
            <EventVideoGallery
              items={[period.featuredVideo]}
              showTitle={false}
              featured
            />
          )}
          {!period.hidePerformances && (
            <EventPerformanceCards items={period.schedule} />
          )}
        </div>
      )}

      {!dark && period.featuredVideo && (
        <div className="mt-10">
          <EventVideoGallery
            items={[period.featuredVideo]}
            showTitle={false}
            featured
          />
        </div>
      )}

      {!period.hidePhotos && (
        <div className="mt-12 md:mt-14">
          <EventPhotoGallery items={period.photos} />
        </div>
      )}
    </section>
  );
}
